export const ADMIN_CONSOLE_ROUTES = {
  AGENT_SETTINGS: '/platform/console/agent-settings',
  DATA_CONFIGURATION: '/platform/console/data-configuration',
  ACCRUALS: '/platform/accruals-settings',
};

export const ADMIN_CONSOLE_NAV = [
  {
    key: 'data-configuration',
    label: 'Data Configuration',
    icon: '/img/navicon/platform-console/nav-data-config.svg',
    href: ADMIN_CONSOLE_ROUTES.DATA_CONFIGURATION,
  },
  {
    key: 'agent-settings',
    label: 'Agent Settings',
    icon: '/img/navicon/platform-console/nav-agent-settings.svg',
    href: ADMIN_CONSOLE_ROUTES.AGENT_SETTINGS,
  },
];

export const ADMIN_CONSOLE_AGENT_TILES = [
  {
    key: 'accruals',
    title: 'Accruals',
    description:
      'Identify unbilled vendor invoices based on open POs automatically to forecast accurate monthly outstanding.',
    route: ADMIN_CONSOLE_ROUTES.ACCRUALS,
  },
];

export const ADMIN_CONSOLE_ACCRUALS_TABS = [
  { key: 'system-instruction', label: 'System Instruction' },
  { key: 'data-ingestion', label: 'Data ingestion' },
  { key: 'column-mapping', label: 'Column mapping' },
  { key: 'activity', label: 'Activity' },
];

export const ADMIN_CONSOLE_INGESTION_FX_SOURCES = [
  { value: 'period_end_spot', label: 'Period-end spot rate' },
  { value: 'period_average', label: 'Period average rate' },
  { value: 'client_provided', label: 'Client-provided rate' },
];

export const ADMIN_CONSOLE_INGESTION_VENDOR_SOURCES = [
  { value: 'erp', label: 'ERP only' },
  { value: 'coupa', label: 'Coupa only' },
  { value: 'merged', label: 'Merged (ERP + Coupa cross-reference)' },
];

export const ADMIN_CONSOLE_INGESTION_RIGHT = 'financialclose_analysis';
export const ADMIN_CONSOLE_FC_RIGHT = 'financialclose';

export const ADMIN_CONSOLE_INGESTION_CURRENCIES = [
  'AED', 'ARS', 'AUD', 'BRL', 'CAD', 'CHF', 'CLP', 'CNY', 'COP', 'CZK', 'DKK', 'EGP', 'EUR', 'GBP', 'HKD',
  'HUF', 'IDR', 'ILS', 'INR', 'JPY', 'KRW', 'MXN', 'MYR', 'NOK', 'NZD', 'PHP', 'PLN', 'RON', 'SAR', 'SEK',
  'SGD', 'THB', 'TRY', 'TWD', 'USD', 'VND', 'ZAR',
];

export const ADMIN_CONSOLE_INGESTION_COPY = {
  emptyBanner: 'This client has no active ingestion configuration. Complete all required fields and save before the first run can be triggered.',
  readOnlyNotice: 'You have view-only access to this configuration. Contact a platform admin to make changes.',
  runNotice: 'A run is currently in progress for this client. Configuration changes will apply from the next run.',
  leavePrompt: 'You have unsaved changes to this client\'s ingestion configuration. Leave without saving?',
  lowHistoryWarning: 'Values below 6 months may cause double-accrual for vendors with Net 60 payment terms or milestone billing. Review the client\'s vendor payment terms before saving.',
  highThresholdNote: 'At this threshold, Noa will auto-post the majority of accruals without human review. Confirm this is appropriate for this client\'s control environment.',
  lowThresholdNote: 'At this threshold, most accruals will be escalated to human review. Noa will produce recommendations but will not post autonomously.',
  apHistoryLabel: 'AP history (months)',
  apHistoryHelp: 'How many months of AP history to pull. Match this to the client\'s longest vendor payment terms.',
  poMinLabel: 'Minimum open PO value (USD)',
  poMinHelp: 'Applied after FX translation — amounts in non-USD currencies are converted to USD before this threshold is evaluated.',
  thresholdLabel: 'Auto-post confidence threshold (%)',
  thresholdHelp: 'Confidence at which Noa may post an accrual without human review. Below this, the item is escalated.',
  fxLabel: 'FX rate source',
  fxPlaceholder: 'Select a rate source',
  nonPoLabel: 'Include non-PO invoices',
  nonPoHelp: 'Non-PO invoices are treated as standalone accrual candidates.',
  poClaimGrainLabel: 'Claim at PO line',
  poClaimGrainHelp: 'Off (0) claims the whole purchase order. On (1) claims each PO line separately. Leave off unless you are testing line-level claim.',
  vendorSourceLabel: 'Vendor master source',
  vendorPlaceholder: 'Select a source',
  mergedHelp: 'The merged option requires a supplier ID mapping table to be uploaded.',
  triggerLabel: 'Period-end trigger (days before cycle end)',
  triggerPlaceholder: '0',
  triggerHelp: 'Runs at T − N, where T is the cycle end date and N is the number of days before that date. 0 means the cycle end date itself. Negative values are not allowed.',
  subsidiariesEmpty: 'Add at least one subsidiary before saving.',
  subsidiaryCode: 'Subsidiary code',
  subsidiaryName: 'Subsidiary name',
  subsidiaryCurrency: 'Currency',
  subsidiaryErp: 'ERP entity reference',
  facetSource: 'Source',
  facetPullType: 'Pull type',
  historyEmpty: 'No saved versions yet.',
};

export const ADMIN_CONSOLE_DATA_CONFIGURATION_TABS = [
  { key: 'purchase-order', label: 'Purchase Order', entityType: 'open_po' },
  { key: 'vendor-master', label: 'Vendor Master', entityType: 'vendor_master' },
  { key: 'accrual-ledger', label: 'Accrual Ledger', entityType: 'accruals_ledger' },
  { key: 'accounts-payable', label: 'Accounts Payable', entityType: 'ap_postings' },
  { key: 'open-invoices', label: 'Unapproved Invoices', entityType: 'open_invoices' },
];

export const ADMIN_CONSOLE_ACCRUALS_CHAR_LIMIT = 2000;

export const ADMIN_CONSOLE_ACCRUALS_INSTRUCTIONS = {
  subtitle: 'Applied to every PO where no vendor-specific instructions exist. Write in plain language.',
  placeholder:
    'Describe how Noa should calculate accruals for this company. Write it the way you would explain it to a new analyst — which records to read, what to compare, and how to treat anything missing.',
  cardTitle: 'Default Noa Instructions',
  cardIcon: '/img/navicon/platform-console/accruals-trial-balance.svg',
  defaultText:
    'Analyse each open purchase order and estimate the value of goods or services received but not yet invoiced as at the period-end date.\n\nWork from the PO line items. Compare ordered quantity and value against goods receipts and invoice history. Where a receipt exists with no matching invoice, accrue the received value. Where neither exists but the service period has partially elapsed, accrue straight-line across the contract term stated on the PO.',
};

export const ADMIN_CONSOLE_ACCRUALS_COLUMN_NAV = [
  { key: 'info-request', label: 'Info Request' },
];

export const ADMIN_CONSOLE_ACCRUALS_COLUMN_OPTIONS = [
  'po_number',
  'po_type',
  'po_status',
  'po_description',
  'subsidiary',
  'subsidiary_code',
  'subsidiary_country',
  'subsidiary_city',
  'functional_currency',
  'netsuite_entity',
  'department',
  'cost_center',
  'gl_account',
  'business_unit',
  'supplier_id',
  'supplier_name',
  'total_po_value_txn_ccy',
  'transaction_currency',
  'invoiced_amount_txn_ccy',
  'open_po_value_txn_ccy',
  'exchange_rate_to_usd',
  'total_po_value_usd',
  'open_po_value_usd',
  'number_of_po_lines',
  'payment_terms',
  'po_created_date',
  'po_approved_date',
  'po_start_date',
  'po_end_date',
  'requisition_number',
  'preparer_name',
  'preparer_email',
  'preparer_title',
  'preparer_employee_id',
  'approver_name',
  'approver_email',
  'approver_title',
  'approver_employee_id',
  'approved_flag',
  'coupa_po_source',
  'ship_to_address',
  'bill_to_address',
  'accrual_required_flag',
];

export const ADMIN_CONSOLE_ACCRUALS_DEFAULT_REQUEST_COLUMNS = [
  'po_number',
  'po_description',
  'supplier_name',
  'open_po_value_txn_ccy',
];

export const ADMIN_CONSOLE_ACCRUALS_ACTIVITY_MODULE_NAMES = [
  'financialclose_accruals',
  'accruals_ingestion',
];

export function buildAdminConsoleRouteQuery(route, clientId) {
  const query = { ...(route?.query || {}) };
  const cid = clientId || route?.query?.cid;
  if (cid) query.cid = String(cid);
  return query;
}
